import React, { useState, useEffect, useRef, useCallback } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { styles } from '../../styles/styles.js';
import {
  getHealth, getLogs,
  triggerScan, triggerPause, triggerResume, triggerExpire, triggerReconcile,
} from '../../services/tradingApi';

const LEVEL_COLORS = {
  ERROR: '#f44336',
  CRITICAL: '#f44336',
  WARNING: '#ff9800',
  INFO: 'white',
  DEBUG: '#888',
};

const LEVEL_OPTIONS = ['ALL', 'ERROR', 'WARNING', 'INFO', 'DEBUG'];

const TradingTerminal = () => {
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState({});

  // Filters
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [componentFilter, setComponentFilter] = useState('ALL');
  const [searchFilter, setSearchFilter] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const logContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchFilter);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchFilter]);

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await getHealth();
      setHealth(data);
    } catch {
      setHealth({ status: 'offline' });
    }
  }, []);

  const [logLimit, setLogLimit] = useState(500);

  const fetchLogs = useCallback(async () => {
    try {
      const params = { limit: logLimit };
      if (levelFilter !== 'ALL') params.level = levelFilter;
      if (componentFilter !== 'ALL') params.component = componentFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const { data } = await getLogs(params);
      setLogs(data.entries);
      setTotalLogs(data.total);
      if (data.components) setComponents(data.components);
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [levelFilter, componentFilter, debouncedSearch, logLimit]);

  useEffect(() => {
    fetchHealth();
    fetchLogs();
  }, [fetchHealth, fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const healthInterval = setInterval(fetchHealth, 10000);
    const logInterval = setInterval(fetchLogs, 5000);
    return () => { clearInterval(healthInterval); clearInterval(logInterval); };
  }, [autoRefresh, fetchHealth, fetchLogs]);

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const handleTrigger = async (name, fn) => {
    setTriggerLoading(prev => ({ ...prev, [name]: true }));
    try {
      await fn();
      fetchHealth();
      fetchLogs();
    } catch (err) {
      console.error(`Trigger ${name} error:`, err);
    } finally {
      setTriggerLoading(prev => ({ ...prev, [name]: false }));
    }
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    return new Date(ts).toLocaleTimeString();
  };

  const isOnline = health?.status === 'running';
  const isEnabled = health?.enabled;

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px' }}>
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Trading Terminal</h2>

      {/* Health status */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '10px' }}>
        <Card.Body style={{ padding: '10px 15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: isOnline ? '#4caf50' : '#f44336',
                display: 'inline-block',
              }} />
              <span style={{ color: 'white', fontWeight: 'bold' }}>
                {isOnline ? 'Running' : 'Offline'}
              </span>
            </span>
            {health?.uptime_seconds && (
              <span style={{ color: '#888', fontSize: '13px' }}>
                Uptime: {Math.floor(health.uptime_seconds / 3600)}h {Math.floor((health.uptime_seconds % 3600) / 60)}m
              </span>
            )}
            {health?.bankroll != null && (
              <span style={{ color: '#888', fontSize: '13px' }}>
                Bankroll: ${health.bankroll}
              </span>
            )}
            <span style={{ color: isEnabled ? '#4caf50' : '#f44336', fontSize: '13px' }}>
              {isEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </Card.Body>
      </Card>

      {/* Trigger buttons */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
        <Button
          size="sm"
          variant="outline-info"
          disabled={triggerLoading.scan}
          onClick={() => handleTrigger('scan', triggerScan)}
        >
          {triggerLoading.scan ? 'Scanning...' : 'Force Scan'}
        </Button>
        {isEnabled ? (
          <Button
            size="sm"
            variant="outline-warning"
            disabled={triggerLoading.pause}
            onClick={() => handleTrigger('pause', triggerPause)}
          >
            {triggerLoading.pause ? 'Pausing...' : 'Pause'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline-success"
            disabled={triggerLoading.resume}
            onClick={() => handleTrigger('resume', triggerResume)}
          >
            {triggerLoading.resume ? 'Resuming...' : 'Resume'}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={triggerLoading.expire}
          onClick={() => handleTrigger('expire', triggerExpire)}
        >
          {triggerLoading.expire ? 'Expiring...' : 'Expire Stale'}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={triggerLoading.reconcile}
          onClick={() => handleTrigger('reconcile', triggerReconcile)}
        >
          {triggerLoading.reconcile ? 'Reconciling...' : 'Reconcile'}
        </Button>
      </div>

      {/* Log filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Form.Select
          size="sm"
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          style={{ width: '120px', backgroundColor: '#111', color: 'white', border: '1px solid #444' }}
        >
          {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
        </Form.Select>
        <Form.Select
          size="sm"
          value={componentFilter}
          onChange={(e) => setComponentFilter(e.target.value)}
          style={{ width: '160px', backgroundColor: '#111', color: 'white', border: '1px solid #444' }}
        >
          <option value="ALL">All Components</option>
          {components.map(c => <option key={c} value={c}>{c}</option>)}
        </Form.Select>
        <Form.Control
          size="sm"
          type="text"
          placeholder="Search logs..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={{ width: '200px', backgroundColor: '#111', color: 'white', border: '1px solid #444' }}
        />
        <Form.Check
          type="checkbox"
          id="auto-scroll"
          label={<span style={{ color: '#888', fontSize: '12px' }}>Auto-scroll</span>}
          checked={autoScroll}
          onChange={(e) => setAutoScroll(e.target.checked)}
        />
        <Form.Check
          type="checkbox"
          id="auto-refresh"
          label={<span style={{ color: '#888', fontSize: '12px' }}>Auto-refresh</span>}
          checked={autoRefresh}
          onChange={(e) => setAutoRefresh(e.target.checked)}
        />
        <Button size="sm" variant="outline-secondary" onClick={fetchLogs}>Refresh</Button>
      </div>

      {/* Log viewer */}
      <div
        ref={logContainerRef}
        style={{
          backgroundColor: '#0a0a0a',
          border: '1px solid #333',
          borderRadius: '4px',
          padding: '10px',
          height: '50vh',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '12px',
          lineHeight: '1.6',
        }}
      >
        {loading ? (
          <div style={{ color: '#888' }}>Loading logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ color: '#888' }}>No log entries found</div>
        ) : (
          logs.map((entry, i) => (
            <div key={entry.id || i} style={{ display: 'flex', gap: '8px', wordBreak: 'break-word' }}>
              <span style={{ color: '#555', minWidth: '30px', textAlign: 'right' }}>{i + 1}</span>
              <span style={{ color: '#888', minWidth: '60px' }}>{timeAgo(entry.timestamp)}</span>
              <span style={{
                color: LEVEL_COLORS[entry.level] || 'white',
                minWidth: '60px',
                fontWeight: entry.level === 'ERROR' || entry.level === 'CRITICAL' ? 'bold' : 'normal',
              }}>
                {entry.level}
              </span>
              <span style={{ color: '#6a9fb5', minWidth: '110px' }}>{entry.component}</span>
              <span style={{ color: 'white' }}>{entry.message}</span>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px', color: '#888', fontSize: '12px' }}>
        <span>{logs.length} of {totalLogs} entries</span>
        {logs.length < totalLogs && (
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setLogLimit(prev => Math.min(prev + 500, 1000))}
            style={{ fontSize: '11px', padding: '2px 8px' }}
          >
            Load More
          </Button>
        )}
      </div>
    </div>
  );
};

export default TradingTerminal;
